import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Archive, Trophy, Calendar, MapPin, Users,
  Loader2, ChevronRight, Medal, Award, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tournament Archive Card
const TournamentArchiveCard = ({ tournament, onViewDetails }) => (
  <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => onViewDetails(tournament)}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            <h3 className="font-semibold">{tournament.name}</h3>
          </div>
          
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground mb-3">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(tournament.end_date).toLocaleDateString()}
            </span>
            {tournament.level && (
              <Badge variant="outline">{tournament.level}</Badge>
            )}
          </div>
          
          {tournament.champion && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <Medal className="h-4 w-4 text-yellow-500" />
              <span className="font-medium text-sm">Champion: {tournament.champion}</span>
              {tournament.final_score && (
                <Badge variant="secondary" className="ml-auto">{tournament.final_score}</Badge>
              )}
            </div>
          )}
        </div>
        
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);

// Tournament Details Modal
const TournamentDetailsModal = ({ tournament, details, open, onClose }) => {
  if (!details) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            {tournament.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Final Standings */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Award className="h-4 w-4" />
              Final Standings
            </h4>
            <div className="space-y-2">
              {details.standings.slice(0, 8).map((team, index) => (
                <div 
                  key={team.team_id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${index === 0 ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-muted/50'}`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-yellow-500 text-white' :
                    index === 1 ? 'bg-gray-400 text-white' :
                    index === 2 ? 'bg-amber-700 text-white' :
                    'bg-muted'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="flex-1 font-medium">{team.team_name}</span>
                  <span className="text-sm text-muted-foreground">
                    {team.wins}W - {team.losses}L
                  </span>
                  <span className="text-sm font-mono">
                    {team.points_for}-{team.points_against}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Game Results */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <History className="h-4 w-4" />
              Game Results
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {details.games.filter(g => g.status === 'completed').map(game => (
                <div key={game.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-sm">
                  <span className={game.winner_id === game.team1_id ? 'font-semibold' : ''}>
                    {game.team1_name}
                  </span>
                  <Badge variant="outline">
                    {game.team1_score} - {game.team2_score}
                  </Badge>
                  <span className={game.winner_id === game.team2_id ? 'font-semibold' : ''}>
                    {game.team2_name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Archives = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentDetails, setTournamentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchArchives();
  }, [levelFilter]);

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (levelFilter !== 'all') params.append('level', levelFilter);
      
      const res = await axios.get(`${API}/archives/tournaments?${params}`);
      setTournaments(res.data);
    } catch (error) {
      console.error('Error fetching archives:', error);
      toast.error('Failed to load archives');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (tournament) => {
    setSelectedTournament(tournament);
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${API}/archives/tournaments/${tournament.id}`);
      setTournamentDetails(res.data);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to load tournament details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const levels = ['District', 'Regional', 'State', 'National'];

  return (
    <div className="container mx-auto px-4 py-8" data-testid="archives-page">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <Archive className="h-8 w-8" />
          Season Archives
        </h1>
        <p className="text-muted-foreground mt-2">
          Browse past tournaments and historical records
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">Tournament Level</label>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger data-testid="level-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {levels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Archives List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : tournaments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Archive className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Archived Tournaments</h3>
            <p className="text-muted-foreground">
              Completed tournaments will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tournaments.map(tournament => (
            <TournamentArchiveCard 
              key={tournament.id} 
              tournament={tournament}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}

      {/* Details Modal */}
      <TournamentDetailsModal
        tournament={selectedTournament}
        details={tournamentDetails}
        open={!!selectedTournament}
        onClose={() => {
          setSelectedTournament(null);
          setTournamentDetails(null);
        }}
      />
    </div>
  );
};
